import { observable, action, computed } from 'mobx';
import Alert from 'react-s-alert';

import { App, ArrayApp, AppRegistry, ModelRegistry } from 'core/index';
import { FormStore } from 'libs/forms/app';

import { clientStore } from 'apps/client/app';
import { PermissionedModel } from 'apps/permissions/app';
import { SpaceModel, SpaceApp } from 'apps/space/app';
import { TagModel, TagsApp } from 'apps/tag/app';
import { SocialAccountModel, SocialAccountsApp } from 'apps/socialAccounts/app';
import { FileModel, FileApp } from 'apps/file/app';
import {
  CreditCardModel,
  CreditCardArrayApp,
  SubscriptionModel,
  SubscriptionsArrayApp,
  InvoiceModel,
  InvoiceArrayApp,
  SeatsUsageModel,
  SeatsUsageApp,
  SubscriptionPreviewModel,
  SubscriptionPreviewApp,
} from 'apps/payment/app';
import { ThreeColumnLayout } from 'apps/layout';
import { modalStore } from 'apps/modals/app';
import { ConnResultsModal } from 'apps/modals/connResultsModal/index';
import { conf as globalConf } from 'apps/config.yaml';
import { conf as tagsConf } from 'apps/tag/config.yaml';
import { raiseUserError } from 'apps/utils';

import { conf } from 'apps/initiative/config.yaml';
import i18next from 'i18next';

const { imageDefaults } = globalConf;

export class InitiativeModel extends PermissionedModel {
  static registryName = 'InitiativeModel';
  static ModelProps = {
    inSpace: SpaceModel,
    avatar: FileModel,
    tags: [TagModel],
    socialAccounts: [SocialAccountModel],
    content: 'ContentModel',
    billingSubscriptions: [SubscriptionModel],
    invoices: [InvoiceModel],
    creditCards: [CreditCardModel],
    seatsUsage: SeatsUsageModel,
    subscriptionPreview: SubscriptionPreviewModel,
    // manufacturer: 'ManufacturerModel',
    // members: [ProfileModel],
  };

  @observable content = null;
  @observable slug = '';
  @observable avatar = null;
  @observable imageFallbackChar = '';
  @observable title = '';
  @observable description = '';
  @observable followingCount = 0;
  @observable inSpace = null;
  @observable socialAccounts = [];
  @observable organizationType = '';
  @observable tags = [];
  @observable billingSubscriptions = [];
  @observable hasValidPaymentMethod = false;
  @observable invoices = [];
  @observable creditCards = [];
  @observable seatsUsage = null;
  @observable subscriptionPreview = null;
  @observable countryCode = '';
  @observable taxId = '';
  @observable projects = null;
  @observable stories = null;
  @observable projectsCount;
  @observable storiesCount;
  @observable threadsCount;
  @observable privateThreadsCount;
  @observable forum;
  @observable manufacturer;
  @observable promotion = null;
}
ModelRegistry.add(InitiativeModel);

export class InitiativeApp extends App {
  static registryName = 'InitiativeApp';
  static AppProps = {
    inSpace: SpaceApp,
    avatar: FileApp,
    tags: TagsApp,
    socialAccounts: SocialAccountsApp,
    content: 'ContentApp',
    billingSubscriptions: SubscriptionsArrayApp,
    invoices: InvoiceArrayApp,
    creditCards: CreditCardArrayApp,
    seatsUsage: SeatsUsageApp,
    subscriptionPreview: SubscriptionPreviewApp,
    // manufacturer: 'ManufacturerApp',
    // members: 'ProfilesApp',
  };

  static Model = InitiativeModel;
  @observable editing = false;
  @observable saving = false;
  @observable followingIsHovered = false;
  @observable followingHoveredCount = 0;

  @computed get route() {
    if (!this.slug) return {};
    return { name: 'initiative', params: { initiative: `${this.slug}` } };
  }

  @computed get hasServices() {
    return this.manufacturer && this.manufacturer.enquiryEmail;
  }

  init() {}

  @action
  AcceptInvitation = invitation => {
    const options = {
      onSuccess: () => {
        clientStore.RemoveInvitation(invitation);
        Alert.success(
          'The invitation has been accepted. Welcome to the organisation!',
        );
      },
      onUserErr: invitation.onError,
      onSysErr: invitation.onError,
    };
    invitation.Accept(options);
  };

  @action
  RejectInvitation = invitation => {
    const options = {
      onSuccess: () => {
        clientStore.RemoveInvitation(invitation);
        Alert.success('The invitation has been rejected');
      },
      onUserErr: invitation.onError,
      onSysErr: invitation.onError,
    };
    invitation.Reject(options);
  };

  @computed
  get imageFallbackPath() {
    if (this.imageFallbackChar) {
      return `/assets/avatars/${this.imageFallbackChar}.jpeg`;
    }
    return imageDefaults.profile;
  }

  @action
  Edit = () => {
    this.editFormStore = new FormStore(this, conf, 'editInitiative');
    this.editing = true;
  };

  @action
  EditTags = () => {
    this.editTagsFormStore = new FormStore(
      this,
      tagsConf,
      'editInitiativeTags',
    );
  };

  @action
  DoneEditingTags = () => {
    modalStore.ClearComponent();
    this.editTagsFormStore = null;
  };

  @action
  Cancel = () => {
    this.ResetLocals();
    this.editing = false;
  };

  @action
  onSaveSuccess = () => {
    this.editing = false;
    this.saving = false;
    this.ResetLocals();
    this.editFormStore.SetPosting(false);
    this.editFormStore.clearDraft();
    Alert.success(
      i18next.t('Your Organisation has been updated successfully.'),
    );
  };

  @action
  Consolidate = () => {
    if (!this.editFormStore.isValid) {
      this.editFormStore.alert();
    } else {
      this.editFormStore.SetPosting(true);
      this.manager.SubmitInitiative({
        onSysErr: this.onSaveSysError,
        onUserErr: this.onSaveUserError,
        onSuccess: this.onSaveSuccess,
      });
    }
  };

  @action
  Save = () => {
    this.saving = true;
    const avatarField = this.editFormStore.fieldStores.get('avatar');
    if (avatarField && avatarField.fileModified) {
      avatarField.getUpdate(this.Consolidate);
    } else {
      this.Consolidate();
    }
  };

  @action
  onSaveUserError = error => {
    this.saving = false;
    this.editFormStore.SetPosting(false);
    raiseUserError(error, 'save', 'Initiative');
  };

  @action
  onSaveSysError = () => {
    this.saving = false;
    Alert.error(
      i18next.t(
        'Hmm... We failed to update your Organisation. Please try again.',
      ),
    );
    this.editFormStore.SetPosting(false);
  };

  @action
  ShowFollowers = () => {
    if (['s', 'xs'].includes(clientStore.activeBreakPoint)) {
      return;
    }
    modalStore.SetModalComponent(ConnResultsModal, {
      arrayApps: [this.manager.followersConn.arrayApp],
      modalTitle: `${this.title}'s followers:`,
    });
  };

  @action
  ShowMembers = () => {
    if (['s', 'xs'].includes(clientStore.activeBreakPoint)) {
      return;
    }
    modalStore.SetModalComponent(ConnResultsModal, {
      arrayApps: [this.manager.membersConn.arrayApp],
      modalTitle: `${this.title}'s members:`,
    });
  };
}
AppRegistry.add(InitiativeApp);

export class InitiativeArrayApp extends ArrayApp {
  static registryName = 'InitiativeArrayApp';
  static ElementApp = InitiativeApp;
  cardLayout = new ThreeColumnLayout();
}
AppRegistry.add(InitiativeArrayApp);
